import { useState, useEffect, useCallback, useRef } from 'react';

const useExamTimer = (initialMinutes = 60) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const intervalRef = useRef(null);
  const onTimeUpRef = useRef(null);

  const startTimer = useCallback((minutes = initialMinutes) => {
    const totalSeconds = minutes * 60;
    setTimeRemaining(totalSeconds);
    setIsActive(true);
    setIsPaused(false);
  }, [initialMinutes]);

  const pauseTimer = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeTimer = useCallback(() => {
    setIsPaused(false);
  }, []);

  const stopTimer = useCallback(() => {
    setIsActive(false);
    setIsPaused(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimeRemaining(0);
  }, [stopTimer]);

  const setOnTimeUp = useCallback((callback) => {
    onTimeUpRef.current = callback;
  }, []);

  // Timer effect
  useEffect(() => {
    if (isActive && !isPaused && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          
          // Time warnings
          if (newTime === 300) { // 5 minutes
            alert('⚠️ 5 minutes remaining!');
          } else if (newTime === 60) { // 1 minute
            alert('⚠️ 1 minute remaining!');
          } else if (newTime === 30) { // 30 seconds
            alert('⚠️ 30 seconds remaining!');
          }
          
          // Time up
          if (newTime <= 0) {
            if (onTimeUpRef.current) {
              onTimeUpRef.current();
            }
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, isPaused, timeRemaining]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getTimeStatus = useCallback(() => {
    if (timeRemaining <= 300) return 'critical'; // Last 5 minutes
    if (timeRemaining <= 600) return 'warning';  // Last 10 minutes
    return 'normal';
  }, [timeRemaining]);

  return {
    timeRemaining,
    isPaused,
    isActive,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    resetTimer,
    setOnTimeUp,
    formatTime: () => formatTime(timeRemaining),
    timeStatus: getTimeStatus()
  };
};

export default useExamTimer;